import { MoreVert as MoreVertIcon } from 'components/icons';
import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { Tooltip } from 'components/ui/tooltip';
import { useDeleteExpenseMutation } from 'store/api/expenses';
import { ExpenseListItem } from 'types/expenses';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';

type Props = {
    expense: ExpenseListItem;
    onDuplicate?: (expense: ExpenseListItem) => void;
};

type MenuItemType = {
    label: string;
    onClick: () => void;
    isDisabled?: boolean;
    disabledTooltip?: string;
    isDestructive?: boolean;
};

const ExpenseActionButtons = ({ expense, onDuplicate }: Props): JSX.Element => {
    const { openModal, closeModal } = useModal();

    const [deleteExpense] = useDeleteExpenseMutation();
    const hasCreateExpensePermission = useHasPermissions(['CREATE.EXPENSES']);
    const isExpenseEntryDisabled = useExpenseEntryDisabled();

    const handleDuplicate = () => {
        if (onDuplicate) {
            onDuplicate(expense);
        }
    };

    const handleDeleteExpense = async () => {
        if (expense.id && !isNaN(Number(expense.id))) {
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={() => {
                        if (expense.id && !isNaN(Number(expense.id))) {
                            void deleteExpense({ id: expense.id })
                                .unwrap()
                                .catch((e) => {
                                    console.error(e);
                                    errorAlert(t('forms.error-submitting-the-form'));
                                })
                                .finally(closeModal);
                        }
                    }}
                    content={<p className="text-body1">{t('expenses.delete-confirmation-text')}</p>}
                />
            );
        }
    };

    const menuItems: MenuItemType[] = [
        {
            label: t('expenses.duplicate'),
            onClick: handleDuplicate,
            isDisabled: !hasCreateExpensePermission || isExpenseEntryDisabled,
            disabledTooltip: isExpenseEntryDisabled ? t('expenses.integration-entry-disabled') : undefined,
        },
        {
            label: t('expenses.delete'),
            isDestructive: true,
            onClick: () => {
                void handleDeleteExpense();
            },
            isDisabled: isExpenseEntryDisabled,
            disabledTooltip: isExpenseEntryDisabled ? t('expenses.integration-entry-disabled') : undefined,
        },
    ];

    const hasActiveMenuItems = menuItems.some((item) => !item.isDisabled || !!item.disabledTooltip);

    const canDeleteExpense = expense.isDeletable;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <TableRowCallForActionButton
                        disabled={!hasActiveMenuItems}
                        shape="rounded"
                        aria-label={t('actions')}
                    >
                        <MoreVertIcon />
                    </TableRowCallForActionButton>
                }
            />
            <DropdownMenuContent align="end" className="w-auto">
                {menuItems.map((item) =>
                    item.isDisabled && !item.disabledTooltip ? null : item.isDisabled && item.disabledTooltip ? (
                        // A disabled item takes no pointer events, so the tooltip hangs off a wrapper.
                        <Tooltip key={`menu-item-${item.label}`} content={item.disabledTooltip} side="left">
                            <div>
                                <DropdownMenuItem disabled variant={item.isDestructive ? 'destructive' : 'default'}>
                                    {item.label}
                                </DropdownMenuItem>
                            </div>
                        </Tooltip>
                    ) : (
                        <DropdownMenuItem
                            key={`menu-item-${item.label}`}
                            variant={item.isDestructive ? 'destructive' : 'default'}
                            onClick={item.onClick}
                            disabled={item.label === t('expenses.delete') ? !canDeleteExpense : false}
                        >
                            {item.label}
                        </DropdownMenuItem>
                    )
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ExpenseActionButtons;
